import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CATEGORY_META,
  DEFAULT_CATEGORY_META,
  contentsForCategory,
  formatTrackCount,
  mergeCategoryCounts,
} from './categoryMeta';
import { ChevronRightIcon } from './icons';
import { TrackCardList } from './RecommendedTracksSection';
import styles from './ExploreCategoriesSection.module.css';

function chipKey(item) {
  return String(item?.id || item?.category || '');
}

function CategoryChip({ item, index, selected, onSelect }) {
  const meta = CATEGORY_META[item.category] || DEFAULT_CATEGORY_META;
  const Icon = meta.Icon;
  const countLabel = formatTrackCount(item.trackCount);

  return (
    <button
      type="button"
      className={styles.categoryChip}
      data-theme={meta.theme}
      style={{ '--chip-i': index }}
      aria-pressed={selected}
      aria-expanded={selected}
      aria-controls={selected ? 'category-contents' : undefined}
      aria-label={`${item.category}, ${countLabel}`}
      onClick={() => onSelect(chipKey(item))}
    >
      <span className={styles.categoryIcon} aria-hidden="true">
        <Icon />
      </span>
      <span className={styles.categoryCopy}>
        <span className={styles.categoryName} title={item.category}>{item.category}</span>
        <span className={styles.categoryCount}>{countLabel}</span>
      </span>
      <span className={styles.categoryChevron} aria-hidden="true">
        <ChevronRightIcon />
      </span>
    </button>
  );
}

function ExploreCategoriesSection({
  categories,
  contents,
  onSelectTrack,
}) {
  const [selectedKey, setSelectedKey] = useState('');
  const listRef = useRef(null);
  const items = useMemo(
    () => mergeCategoryCounts(categories, contents),
    [categories, contents],
  );
  const totalTracks = useMemo(
    () => items.reduce((sum, item) => sum + item.trackCount, 0),
    [items],
  );
  const selectedItem = useMemo(
    () => items.find((item) => chipKey(item) === selectedKey) || null,
    [items, selectedKey],
  );
  const categoryContents = useMemo(
    () => (selectedItem ? contentsForCategory(contents, selectedItem.category) : []),
    [contents, selectedItem],
  );

  useEffect(() => {
    if (!selectedKey) return;
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedKey]);

  return (
    <section className={styles.exploreSection} aria-labelledby="explore-categories-title">
      <div className={styles.exploreHeader}>
        <div className={styles.exploreHeading}>
          <h2 id="explore-categories-title" className={styles.exploreTitle}>
            Explore Categories
          </h2>
          <p className={styles.exploreSubtitle}>
            Browse all topics and find the right guidance for your unique transition.
          </p>
        </div>
        <p className={styles.exploreBadge}>
          {totalTracks} active {totalTracks === 1 ? 'Quick Richuel' : 'Quick Richuels'}
        </p>
      </div>

      <div className={styles.exploreGrid}>
        {items.map((item, index) => (
          <CategoryChip
            key={chipKey(item)}
            item={item}
            index={index}
            selected={chipKey(item) === selectedKey}
            onSelect={(key) => setSelectedKey((current) => (current === key ? '' : key))}
          />
        ))}
      </div>

      {selectedItem ? (
        <div
          id="category-contents"
          ref={listRef}
          className={styles.categoryContents}
          aria-live="polite"
          aria-labelledby="category-contents-title"
        >
          <h3 id="category-contents-title" className={styles.categoryContentsTitle}>
            {selectedItem.category}
          </h3>
          {categoryContents.length === 0 ? (
            <p className={styles.categoryContentsEmpty}>No Quick Richuel in this category yet.</p>
          ) : (
            <TrackCardList
              tracks={categoryContents}
              onSelectTrack={onSelectTrack}
              label={selectedItem.category}
            />
          )}
        </div>
      ) : null}
    </section>
  );
}

export default ExploreCategoriesSection;
